/**
 * SEO meta clamping helpers.
 * <title> / og:title: 50-60 chars.
 * <meta description> / og:description: 140-160 chars.
 *
 * Regras rígidas:
 * - Nunca cortar no meio de uma palavra (sempre no espaço anterior).
 * - Nunca deixar pontuação órfã no fim (`,` `;` `:` `-` `—` `|`).
 * - Se não conseguir chegar no mínimo sem cortar palavra, preferimos
 *   um texto mais curto e correto a um texto "esticado" cortado.
 */

export const SEO_TITLE_MIN = 50;
export const SEO_TITLE_MAX = 60;
export const SEO_DESC_MIN = 140;
export const SEO_DESC_MAX = 160;

const BRAND_SHORT = "Dedetizadora em Chapecó";
const TITLE_PADDINGS = [
  ` | ${BRAND_SHORT}`,
  " — Controle de Pragas",
  " — Chapecó/SC",
  " em 2026",
  " no Oeste de SC",
];
const DESC_PADDINGS = [
  "Controle profissional de pragas urbanas em Chapecó e região com equipe técnica.",
  "Dedetização, desratização e descupinização com produtos registrados e garantia.",
  "Atendimento residencial, comercial e industrial em Chapecó/SC.",
];

/** Corta em `max` sempre num limite de palavra e limpa pontuação órfã. */
function truncateAtWord(input: string, max: number): string {
  const s = input.replace(/\s+/g, " ").trim();
  if (s.length <= max) return cleanTail(s);
  const cut = s.slice(0, max + 1); // +1 para saber se o char seguinte é espaço
  // Se o caractere logo após `max` é espaço, o corte já é limpo.
  const boundary = /\s/.test(cut[max]) ? max : cut.lastIndexOf(" ");
  const trimmed = boundary > 0 ? cut.slice(0, boundary) : cut.slice(0, max);
  return cleanTail(trimmed);
}

function cleanTail(s: string): string {
  return s.replace(/[\s\-–—|,;:]+$/u, "").trim();
}

const hasBrand = (s: string) => /dedetizadora\s*em\s*chapec/i.test(s);

/** Adiciona padding INTEIRO (nunca corta o padding no meio de palavra). */
function padUntilMin(base: string, paddings: string[], min: number, max: number, allowBrand = true): string {
  let out = base;
  for (const pad of paddings) {
    if (out.length >= min) break;
    if (!allowBrand && /dedetizadora\s*em\s*chapec/i.test(pad)) continue;
    if (pad.trim().startsWith("|") && hasBrand(out)) continue;
    const sep = /[.!?]$/.test(out) || pad.startsWith(" ") || pad.startsWith(" |") ? "" : " ";
    const next = `${out}${sep}${pad}`.replace(/\s+/g, " ");
    if (next.length <= max) out = next;
  }
  return cleanTail(out);
}

export function seoTitle(raw: string | null | undefined): string {
  let t = (raw ?? "").replace(/\s+/g, " ").trim();
  if (!t) t = BRAND_SHORT;

  // 1. Se passou do máximo, corta no limite de palavra preservando o sufixo
  //    " | Brand" quando possível.
  if (t.length > SEO_TITLE_MAX) {
    const barIdx = t.lastIndexOf(" | ");
    if (barIdx > 0) {
      const head = t.slice(0, barIdx).trim();
      const tail = t.slice(barIdx);
      const roomForHead = SEO_TITLE_MAX - tail.length;
      t = roomForHead >= 20 ? truncateAtWord(head, roomForHead) + tail : truncateAtWord(t, SEO_TITLE_MAX);
    } else {
      t = truncateAtWord(t, SEO_TITLE_MAX);
    }
  }

  // 2. Padding só com blocos inteiros; nunca cortamos padding no meio.
  if (t.length < SEO_TITLE_MIN) {
    t = padUntilMin(t, TITLE_PADDINGS, SEO_TITLE_MIN, SEO_TITLE_MAX);
  }

  // 3. Segurança extra: se por acaso ficou acima do máximo (concat errado),
  //    corta de novo no limite de palavra.
  if (t.length > SEO_TITLE_MAX) t = truncateAtWord(t, SEO_TITLE_MAX);
  return cleanTail(t);
}

export function seoDescription(raw: string | null | undefined): string {
  let d = (raw ?? "").replace(/\s+/g, " ").trim();
  if (!d) d = "Dedetizadora em Chapecó: controle de baratas, ratos, cupins e insetos com equipe técnica e produtos registrados.";

  if (d.length > SEO_DESC_MAX) return ensureTerminal(truncateAtWord(d, SEO_DESC_MAX));

  if (d.length < SEO_DESC_MIN) {
    d = padUntilMin(d, DESC_PADDINGS, SEO_DESC_MIN, SEO_DESC_MAX);
  }

  if (d.length > SEO_DESC_MAX) d = truncateAtWord(d, SEO_DESC_MAX);
  return ensureTerminal(d);
}

/** Garante que a descrição termine em pontuação natural sem estourar `max`. */
function ensureTerminal(s: string): string {
  const clean = cleanTail(s);
  if (/[.!?]$/.test(clean)) return clean;
  if (clean.length < SEO_DESC_MAX) return `${clean}.`;
  // Se já está no limite, tira uma palavra e adiciona ponto final.
  const lastSpace = clean.lastIndexOf(" ");
  if (lastSpace > SEO_DESC_MIN - 5) return `${cleanTail(clean.slice(0, lastSpace))}.`;
  return clean; // último recurso: mantém sem ponto
}
